import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Carousel, useCarousel } from '../Carousel';
import * as CarouselStyled from '../Carousel/Carousel.styles';
import { Content, ContentWrapper } from '../Content';
import FAQItem from './components/FAQItem/FAQItem';
import { ITEMS_PER_PAGE } from './data';
import * as Styled from './FAQ.styles';

const parseFaqAnswer = (answer) =>
  answer
    .split(/<br\s*\/?>\s*<br\s*\/?>/i)
    .map((paragraph) => paragraph.replace(/<br\s*\/?>/gi, ' ').trim())
    .filter(Boolean);

const chunkItems = (items, size) => {
  const pages = [];

  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size));
  }

  return pages;
};

const FAQ = () => {
  const { t } = useTranslation();

  const items = useMemo(() => {
    const faqRaw = t('faq', { returnObjects: true });

    return Object.entries(faqRaw).map(([id, item]) => ({
      id: Number(id),
      question: item.question,
      answer: parseFaqAnswer(item.answer),
    }));
  }, [t]);

  const pages = useMemo(() => chunkItems(items, ITEMS_PER_PAGE), [items]);
  const [openId, setOpenId] = useState(() => items[0]?.id ?? null);

  const {
    slidesPerView,
    gap,
    offset,
    scrollPrev,
    scrollNext,
    canScrollPrev,
    canScrollNext,
  } = useCarousel(pages.length, { slidesPerView: 1 });

  const handleToggle = (id) => {
    setOpenId((current) => (current === id ? null : id));
  };

  const handlePageChange = (direction) => {
    if (direction === 'prev') {
      scrollPrev();
    } else {
      scrollNext();
    }
    setOpenId(null);
  };

  return (
    <Styled.FAQ id="faq">
      <ContentWrapper>
        <Content labels={[t('homeFaq.label')]} title={t('homeFaq.title')} />

        <Styled.SliderWrap>
          <Carousel>
            <Carousel.Viewport>
              <Carousel.Container offset={offset}>
                {pages.map((pageItems, pageIndex) => (
                  <Carousel.Item
                    key={pageIndex}
                    slidesPerView={slidesPerView}
                    gap={gap}
                  >
                    <Styled.List>
                      {pageItems.map((item, localIndex) => {
                        const globalIndex =
                          pageIndex * ITEMS_PER_PAGE + localIndex;

                        return (
                          <FAQItem
                            key={item.id}
                            item={item}
                            index={globalIndex}
                            isOpen={openId === item.id}
                            onToggle={() => handleToggle(item.id)}
                          />
                        );
                      })}
                    </Styled.List>
                  </Carousel.Item>
                ))}
              </Carousel.Container>
            </Carousel.Viewport>

            <CarouselStyled.Nav>
              <Carousel.Previous
                onClick={() => handlePageChange('prev')}
                disabled={!canScrollPrev}
                aria-label={t('homeFaq.prevPage')}
              />
              <Carousel.Next
                onClick={() => handlePageChange('next')}
                disabled={!canScrollNext}
                aria-label={t('homeFaq.nextPage')}
              />
            </CarouselStyled.Nav>
          </Carousel>
        </Styled.SliderWrap>
      </ContentWrapper>
    </Styled.FAQ>
  );
};

export default FAQ;
